import React, { useRef, useEffect } from 'react';
import styles from './styles.module.scss';
import { MagicPen, CloseCircleGreyIcon } from 'assets';
import { useClickOutside } from 'hooks';
import { Resolver, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import TypeWriter from 'components/typewriter';

const schema = yup.object().shape({
  prompt: yup.string().optional(),
});

interface RefineModalProps {
  isOpen: boolean;
  onClose: () => void;
  modalText: string;
  refinedText: string;
  onRefine: (data: string) => void;
  onInsert: (index: number) => void;
  isLoading: boolean;
  showInput?: boolean;
}

const RefineModal = ({
  isOpen,
  onClose,
  modalText,
  refinedText,
  onRefine,
  onInsert,
  isLoading,
  showInput = false,
}: RefineModalProps) => {
  const modalRef = useRef(null);
  useClickOutside(modalRef, onClose);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema) as Resolver<{prompt: string}>,
    defaultValues: { prompt: '' },
  });

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const onSubmit = (data: { prompt: string | undefined }) => {
    if (data.prompt) {onRefine(data.prompt);}  else {onRefine('');}
    reset();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} ref={modalRef}>
        <div className={styles.modalTitleContainer}>
          <div>
            <MagicPen className={styles.modalIcon} />
            <h2 className={styles.modalTitle}>Help me</h2>
          </div>
          <CloseCircleGreyIcon className={styles.closeButton} onClick={onClose} />
        </div>

        <p className={styles.modalText}>
          <TypeWriter text={refinedText ? refinedText : modalText} delay={0} id={''} />
        </p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.modalFooter}>
            {showInput && (
              <div style={{ width: '100%' }}>
                <input
                  type="text"
                  placeholder="Enter prompt"
                  className={styles.promptInput}
                  {...register('prompt')}
                />
                {errors.prompt && <p className={styles.error}>{errors.prompt.message}</p>}
              </div>
            )}
            <div className={styles.modalButtons}>
              <button
                type="submit"
                className={refinedText ? `${styles.refineAltButton}` : `${styles.refineButton} ${styles.disabled}`}
                disabled={isLoading}
              >
                {isLoading ? 'Refining...' : 'Refine'}
              </button>
              {refinedText && (
                <button
                  type="button"
                  className={styles.insertButton}
                  onClick={() => onInsert(0)}
                >
                  Insert
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RefineModal;